import { Routes } from '@angular/router';

import { CamRoutes } from '@camelot/menu';
import { FeatureGuard } from '@camelot/user';

import { EAutomationsRoute, automationMenuRoutes, automationsRoutes } from './features/automations/automations.routes';
import {
  EValidationsFlowRoute,
  validationsFlowMenuRoutes,
  validationsFlowsRoutes,
} from './features/validations-flow/validations-flow.routes';

export enum ESettingsRoute {
  settings = 'settings',
  teams = 'teams',
  accounting = 'accounting',
  taskTypes = 'taskTypes',
  view = 'view',
  list = 'list',
  communications = 'communications',
  companies = 'companies',
  users = 'users',
  functions = 'functions',
  impersonation = 'impersonation',
  noteTypes = 'noteTypes',
  colorSets = 'colorSets',
  bank = 'bank',
  peppol = 'peppol',
  msIntegrations = 'msIntegrations',
  ysIntegration = 'ysIntegration',
  organization = 'organization',
  departments = 'departments',
  activities = 'activities',
  vatLevels = 'vatLevels',
}

CamRoutes.addRoute({
  key: ESettingsRoute.settings,
  url: 'settings',
  children: [
    {
      key: ESettingsRoute.teams,
      url: 'teams',
    },
    {
      key: ESettingsRoute.organization,
      url: 'organization',
      children: [
        {
          key: ESettingsRoute.companies,
          url: 'companies',
          children: [
            {
              key: ESettingsRoute.view,
              url: 'view/:companyId',
              children: [
                {
                  key: ESettingsRoute.accounting,
                  url: 'integrations',
                  children: [
                    {
                      key: ESettingsRoute.view,
                      url: 'view/:integrationId',
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          key: ESettingsRoute.departments,
          url: 'departments',
        },
        {
          key: ESettingsRoute.activities,
          url: 'activities',
        },
      ],
    },
    {
      key: ESettingsRoute.taskTypes,
      url: 'taskTypes',
    },
    {
      key: ESettingsRoute.noteTypes,
      url: 'noteTypes',
    },
    {
      key: ESettingsRoute.colorSets,
      url: 'colorSets',
    },
    {
      key: ESettingsRoute.vatLevels,
      url: 'vatLevels',
    },
    {
      key: ESettingsRoute.msIntegrations,
      url: 'msIntegrations',
    },
    {
      key: ESettingsRoute.ysIntegration,
      url: 'ysIntegration',
    },
    {
      key: ESettingsRoute.communications,
      url: 'communications',
      children: [
        {
          key: ESettingsRoute.list,
          url: 'list',
        },
      ],
    },
    automationMenuRoutes,
    validationsFlowMenuRoutes,
    {
      key: ESettingsRoute.users,
      url: 'users',
      children: [
        {
          key: ESettingsRoute.list,
          url: 'list',
        },
        {
          key: ESettingsRoute.view,
          url: 'view/:id',
        },
      ],
    },
    {
      key: ESettingsRoute.functions,
      url: 'functions',
      children: [
        {
          key: ESettingsRoute.list,
          url: 'list',
        },
        {
          key: ESettingsRoute.view,
          url: 'view/:id',
        },
      ],
    },
    {
      key: ESettingsRoute.impersonation,
      url: 'impersonation',
    },
  ],
});

export const settingsRoutes: Routes = [
  {
    path: '',
    loadComponent: () => import('./pages/settings.component').then(c => c.SettingsPage),
    children: [
      {
        path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.teams]),
        loadComponent: () => import('./pages/teams/list/teams.component').then(c => c.TeamsPage),
        canActivate: [FeatureGuard],
        data: { feature: 'ticketing', level: 'authorize', permissions: ['manage:task'] },
      },
      {
        path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.organization]),
        canActivate: [FeatureGuard],
        data: { permissions: ['read:organization'] },
        children: [
          {
            path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.organization, ESettingsRoute.companies]),
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/organization/companies/list/organization-companies.component').then(
                    c => c.OrganizationCompaniesPage
                  ),
              },
              {
                path: CamRoutes.getUrl([
                  ESettingsRoute.settings,
                  ESettingsRoute.organization,
                  ESettingsRoute.companies,
                  ESettingsRoute.view,
                ]),
                canActivate: [FeatureGuard],
                data: { feature: 'chift', level: 'authorize' },
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./pages/organization/companies/view/organization-company-view.component').then(
                        c => c.OrganizationCompanyViewPage
                      ),
                    // Littéraux calqués sur ECompanyMenuKey : l'importer créerait un cycle avec company-tabs.ts.
                    children: [
                      {
                        path: '',
                        redirectTo: 'identity',
                        pathMatch: 'full',
                      },
                      {
                        path: 'identity',
                        loadComponent: () =>
                          import(
                            './pages/organization/companies/view/tabs/organization-company-tab-identity.component'
                          ).then(c => c.OrganizationCompanyTabIdentityPage),
                      },
                      {
                        path: 'numbering',
                        loadComponent: () =>
                          import(
                            './pages/organization/companies/view/tabs/organization-company-tab-numbering.component'
                          ).then(c => c.OrganizationCompanyTabNumberingPage),
                      },
                      {
                        path: 'billing',
                        loadComponent: () =>
                          import(
                            './pages/organization/companies/view/tabs/organization-company-tab-billing.component'
                          ).then(c => c.OrganizationCompanyTabBillingPage),
                      },
                      {
                        path: 'pdf-template',
                        loadComponent: () =>
                          import(
                            './pages/organization/companies/view/tabs/organization-company-tab-pdf-template.component'
                          ).then(c => c.OrganizationCompanyTabPdfTemplatePage),
                      },
                      {
                        path: 'accounting',
                        loadComponent: () =>
                          import(
                            './pages/organization/companies/view/tabs/organization-company-tab-accounting.component'
                          ).then(c => c.OrganizationCompanyTabAccountingPage),
                      },
                      {
                        path: 'peppol',
                        loadComponent: () =>
                          import(
                            './pages/organization/companies/view/tabs/organization-company-tab-peppol.component'
                          ).then(c => c.OrganizationCompanyTabPeppolPage),
                      },
                      {
                        path: 'bank',
                        loadComponent: () =>
                          import(
                            './pages/organization/companies/view/tabs/organization-company-tab-bank.component'
                          ).then(c => c.OrganizationCompanyTabBankPage),
                      },
                      {
                        path: 'mandate',
                        loadComponent: () =>
                          import(
                            './pages/organization/companies/view/tabs/organization-company-tab-mandate.component'
                          ).then(c => c.OrganizationCompanyTabMandatePage),
                      },
                    ],
                  },
                  {
                    path: CamRoutes.getUrl([
                      ESettingsRoute.settings,
                      ESettingsRoute.organization,
                      ESettingsRoute.companies,
                      ESettingsRoute.view,
                      ESettingsRoute.accounting,
                    ]),
                    children: [
                      {
                        path: CamRoutes.getUrl([
                          ESettingsRoute.settings,
                          ESettingsRoute.organization,
                          ESettingsRoute.companies,
                          ESettingsRoute.view,
                          ESettingsRoute.accounting,
                          ESettingsRoute.view,
                        ]),
                        loadComponent: () =>
                          import('./pages/integrations/view/integration-detail.component').then(
                            c => c.IntegrationDetailPage
                          ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.organization, ESettingsRoute.departments]),
            loadComponent: () =>
              import('./pages/organization/departments/list/organization-departments.component').then(
                c => c.OrganizationDepartmentsPage
              ),
          },
          {
            path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.organization, ESettingsRoute.activities]),
            loadComponent: () =>
              import('./pages/organization/activities/list/organization-activities.component').then(
                c => c.OrganizationActivitiesPage
              ),
          },
        ],
      },
      {
        path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.taskTypes]),
        loadComponent: () => import('./pages/task-types/list/task-types.component').then(c => c.TaskTypesPage),
        canActivate: [FeatureGuard],
        data: { feature: 'ticketing', level: 'authorize', permissions: ['manage:task'] },
      },
      {
        canActivate: [FeatureGuard],
        data: { permissions: ['manage:setting'] },
        loadComponent: () => import('./pages/note-types/list/note-types.component').then(c => c.NoteTypesPage),
        path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.noteTypes]),
      },
      {
        canActivate: [FeatureGuard],
        data: { permissions: ['manage:setting'] },
        loadComponent: () => import('./pages/color-sets/list/color-sets.component').then(c => c.ColorSetsPage),
        path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.colorSets]),
      },
      {
        canActivate: [FeatureGuard],
        data: { permissions: ['manage:setting'] },
        loadComponent: () => import('./pages/vat-levels/vat-levels.component').then(c => c.VatLevelsPage),
        path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.vatLevels]),
      },
      {
        canActivate: [FeatureGuard],
        data: { permissions: ['manage:setting'] },
        loadComponent: () =>
          import('./pages/microsoft-integrations/view/microsoft-integration-view.component').then(
            c => c.MicrosoftIntegrationViewPage
          ),
        path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.msIntegrations]),
      },
      {
        canActivate: [FeatureGuard],
        data: { permissions: ['manage:setting'] },
        loadComponent: () =>
          import('./pages/yousign-integration/view/you-sign-integration-view.component').then(
            c => c.YouSignIntegrationViewPage
          ),
        path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.ysIntegration]),
      },
      {
        path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.communications]),
        canActivate: [FeatureGuard],
        data: { permissions: ['manage:setting'] },
        children: [
          {
            path: '',
            redirectTo: CamRoutes.getAbsoluteUrl([
              ESettingsRoute.settings,
              ESettingsRoute.communications,
              ESettingsRoute.list,
            ]),
            pathMatch: 'full',
          },
          {
            path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.communications, ESettingsRoute.list]),
            loadComponent: () =>
              import('./pages/communications/templates/communications-templates-list.component').then(
                c => c.CommunicationsTemplatesListPage
              ),
          },
        ],
      },
      {
        path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.users]),
        canActivate: [FeatureGuard],
        data: { permissions: ['read:user'] },
        children: [
          {
            path: '',
            redirectTo: CamRoutes.getAbsoluteUrl([ESettingsRoute.settings, ESettingsRoute.users, ESettingsRoute.list]),
            pathMatch: 'full',
          },
          {
            path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.users, ESettingsRoute.list]),
            loadComponent: () => import('./pages/users/list/users.component').then(c => c.UsersPage),
          },
          {
            path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.users, ESettingsRoute.view]),
            loadComponent: () => import('./pages/users/view/user-view.component').then(c => c.UserViewPage),
          },
        ],
      },
      {
        path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.functions]),
        canActivate: [FeatureGuard],
        data: { permissions: ['manage:user'] },
        children: [
          {
            path: '',
            redirectTo: CamRoutes.getAbsoluteUrl([
              ESettingsRoute.settings,
              ESettingsRoute.functions,
              ESettingsRoute.list,
            ]),
            pathMatch: 'full',
          },
          {
            path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.functions, ESettingsRoute.list]),
            loadComponent: () => import('./pages/functions/list/functions.component').then(c => c.FunctionsPage),
          },
          {
            path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.functions, ESettingsRoute.view]),
            loadComponent: () => import('./pages/functions/view/function-view.component').then(c => c.FunctionViewPage),
          },
        ],
      },
      {
        canActivate: [FeatureGuard],
        children: automationsRoutes(ESettingsRoute.settings),
        data: { permissions: ['read:automation'] },
        path: CamRoutes.getUrl([ESettingsRoute.settings, EAutomationsRoute.automations]),
      },
      {
        canActivate: [FeatureGuard],
        children: validationsFlowsRoutes(ESettingsRoute.settings),
        data: { feature: 'validation', level: 'authorize', permissions: ['read:validation'] },
        path: CamRoutes.getUrl([ESettingsRoute.settings, EValidationsFlowRoute.validationsFlow]),
      },
      {
        canActivate: [FeatureGuard],
        data: { permissions: ['manage:user'] },
        loadComponent: () => import('./pages/impersonation/impersonation.component').then(c => c.ImpersonationPage),
        path: CamRoutes.getUrl([ESettingsRoute.settings, ESettingsRoute.impersonation]),
      },
    ],
  },
];
